import { create } from 'zustand';
import { adminOrganizationManagementApi } from '@api/api-config.ts';
import { AdminOrganizationDetailResponse, OrganizationFeature } from '@api/generated/portal-be-admin';

interface AdminOrganizationDetailState {
  organization: AdminOrganizationDetailResponse | null;
  loading: boolean;
  error: string | null;
}

interface AdminOrganizationDetailActions {
  loadOrganizationDetail: (organizationId: number) => Promise<void>;
  updateOrganizationType: (organizationId: number, isSupplier: boolean, isClient: boolean) => Promise<boolean>;
  updateOrganizationFeature: (organizationId: number, feature: OrganizationFeature, enabled: boolean) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

export const useAdminOrganizationDetailStore = create<AdminOrganizationDetailState & AdminOrganizationDetailActions>(set => ({
  organization: null,
  loading: false,
  error: null,

  loadOrganizationDetail: async (organizationId: number) => {
    set({ loading: true, error: null });

    try {
      const response = await adminOrganizationManagementApi.getOrganizationDetail({ id: organizationId });
      set({
        organization: response.data,
        loading: false,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst detail organizace';
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  updateOrganizationType: async (organizationId: number, isSupplier: boolean, isClient: boolean) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganizationType({
        id: organizationId,
        adminUpdateOrganizationTypeRequest: { isSupplier, isClient },
      });
      set({ organization: response.data });
      return true;
    } catch (err: any) {
      set({ error: err.response?.data?.message || 'Nepodařilo se aktualizovat typ organizace' });
      return false;
    }
  },

  updateOrganizationFeature: async (organizationId: number, feature: OrganizationFeature, enabled: boolean) => {
    try {
      // BE vrací celý detail, takže se rovnou promítne i změněná platnost přiřazení.
      const response = await adminOrganizationManagementApi.updateOrganizationFeature({
        id: organizationId,
        feature,
        adminUpdateOrganizationFeatureRequest: { enabled },
      });
      set({ organization: response.data });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se změnit prémiovou funkci' });
      return false;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      organization: null,
      loading: false,
      error: null,
    });
  },
}));
